import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { api, ApiError, fieldIssues } from '../api';
import { can, type Me } from '../auth/me';
import { RowMenu } from '../ui/RowMenu';
import { SCHEDULE_KEY, STAGE_TYPES_RESOURCE, type StageType } from './types';
import { leaveGuarded, useUnsavedGuard } from '../ui/unsaved';

export const STAGE_TYPES_KEY = [...SCHEDULE_KEY, 'stage-types'] as const;

export function useStageTypes(enabled = true) {
  return useQuery({ queryKey: STAGE_TYPES_KEY, enabled, queryFn: () => api<{ stageTypes: StageType[] }>('/api/schedule/stage-types') });
}

function problem(error: unknown): string | null {
  if (!(error instanceof ApiError)) return null;
  if (error.status === 409) return 'Ktoś już zmienił tę listę. Zamknij i otwórz ją jeszcze raz.';
  return fieldIssues(error).map((i) => i.message).join(' ') || error.message;
}

/**
 * "Rodzaje etapów" (gear in the "Harmonogram" tab and "Ustawienia firmy"): the owner's list of stage kinds.
 * A deleted kind stays on the stages that have it; the order is the order of the list when adding a stage.
 */
export function StageTypesSettings({ me, heading = true }: { me: Me; heading?: boolean }) {
  const queryClient = useQueryClient();
  const types = useStageTypes();
  const [name, setName] = useState('');
  const [editing, setEditing] = useState<{ id: string; name: string } | null>(null);
  const refresh = () => queryClient.invalidateQueries({ queryKey: SCHEDULE_KEY });
  const mayAdd = can(me, STAGE_TYPES_RESOURCE, 'create');
  const mayEdit = can(me, STAGE_TYPES_RESOURCE, 'edit');
  const mayDelete = can(me, STAGE_TYPES_RESOURCE, 'delete');

  const add = useMutation({
    mutationFn: () => api<StageType>('/api/schedule/stage-types', { method: 'POST', body: { name: name.trim() } }),
    onSuccess: async () => {
      setName('');
      await refresh();
    },
  });
  const save = useMutation({
    mutationFn: ({ t, body }: { t: StageType; body: Record<string, unknown> }) => api<StageType>(`/api/schedule/stage-types/${t.id}`, { method: 'PATCH', body: { version: t.version, ...body } }),
    onSuccess: async () => {
      setEditing(null);
      await refresh();
    },
  });
  const remove = useMutation({
    mutationFn: (t: StageType) => api(`/api/schedule/stage-types/${t.id}?version=${t.version}`, { method: 'DELETE' }),
    onSuccess: refresh,
  });

  const list = types.data?.stageTypes ?? [];
  /** Swaps the places of two neighbours: both get their new position in the list. */
  const move = async (i: number, dir: -1 | 1) => {
    const a = list[i];
    const b = list[i + dir];
    if (!a || !b) return;
    await save.mutateAsync({ t: a, body: { sort: i + dir + 1 } }).catch(() => undefined);
    await save.mutateAsync({ t: b, body: { sort: i + 1 } }).catch(() => undefined);
  };
  const submit = (e: FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (name.trim() !== '') add.mutate();
  };
  // owner 2026-10-23 21:14: leaving with unsaved changes asks „Zapisz / Nie zapisuj / Anuluj”
  const addRef = useUnsavedGuard({
    dirty: name.trim() !== '',
    save: async () => {
      await add.mutateAsync();
      return true;
    },
  });
  const renamed = editing ? (types.data?.stageTypes ?? []).find((t) => t.id === editing.id) : undefined;
  const editRef = useUnsavedGuard({
    dirty: !!editing && !!renamed && editing.name.trim() !== renamed.name,
    save: async () => {
      if (!editing || !renamed || editing.name.trim() === '') return false;
      await save.mutateAsync({ t: renamed, body: { name: editing.name.trim() } });
      return true;
    },
  });
  const error = problem(add.error) ?? problem(save.error) ?? problem(remove.error);

  return (
    <section className="settings-section" aria-label="Rodzaje etapów harmonogramu" data-feature-id="schedule.stage_types">
      {heading && <h2>Rodzaje etapów harmonogramu</h2>}
      <p className="hint">Z tej listy wybierasz etap w zakładce „Harmonogram” wydarzenia. Usunięty rodzaj zostaje na etapach, które już go mają.</p>
      {types.isLoading && <p className="muted" role="status">Wczytywanie…</p>}
      <ul className="settings-list" aria-label="Rodzaje etapów">
        {list.map((t, i) => (
          <li key={t.id} className="settings-row">
            {editing?.id === t.id ? (
              <form
                ref={editRef}
                className="stage-type-edit settings-row-main"
                aria-label={`Zmiana nazwy: ${t.name}`}
                onSubmit={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (editing.name.trim() !== '') save.mutate({ t, body: { name: editing.name.trim() } });
                }}
              >
                <input autoFocus value={editing.name} onChange={(e) => setEditing({ id: t.id, name: e.target.value })} aria-label="Nazwa etapu" maxLength={100} />
                <button className="btn small primary" type="submit" disabled={save.isPending}>
                  Zapisz
                </button>
                <button className="btn small" type="button" onClick={() => setEditing(null)}>
                  Anuluj
                </button>
              </form>
            ) : (
              <>
                <span className="settings-row-main">{t.name}</span>
                {mayEdit && (
                  <span className="stage-type-move">
                    <button className="btn small ghost icon" type="button" aria-label={`Wyżej: ${t.name}`} disabled={i === 0 || save.isPending} onClick={() => void move(i, -1)}>
                      ↑
                    </button>
                    <button className="btn small ghost icon" type="button" aria-label={`Niżej: ${t.name}`} disabled={i === list.length - 1 || save.isPending} onClick={() => void move(i, 1)}>
                      ↓
                    </button>
                  </span>
                )}
                {(mayEdit || mayDelete) && (
                  <RowMenu
                    label={`Więcej działań: ${t.name}`}
                    actions={[
                      ...(mayEdit ? [{ label: 'Zmień nazwę', onSelect: () => leaveGuarded(() => setEditing({ id: t.id, name: t.name })) }] : []),
                      ...(mayDelete ? [{ label: 'Usuń', danger: true, onSelect: () => remove.mutate(t) }] : []),
                    ]}
                  />
                )}
              </>
            )}
          </li>
        ))}
      </ul>
      {mayAdd && (
        <form ref={addRef} className="stage-type-add" aria-label="Nowy rodzaj etapu" onSubmit={submit}>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="np. Spotkanie z organizatorem" aria-label="Nazwa nowego etapu" maxLength={100} autoComplete="off" />
          <button className="btn small" type="submit" disabled={add.isPending || name.trim() === ''}>
            Dodaj
          </button>
        </form>
      )}
      {error && (
        <p className="alert" role="alert">
          {error}
        </p>
      )}
    </section>
  );
}
